/**
 * telemetryScrub — strip personal data from a Sentry event before it
 * leaves the browser.
 *
 * Same file as the learner site's (playbook-web src/lib/telemetryScrub.ts),
 * so all three apps scrub by one rule: change it there and here together.
 *
 * Product, 2 Oct 2026 (board item error-alerts-silent): Sentry goes on
 * for preview, and nothing personal may reach it — no names, emails,
 * learner ids in plain text, recordings or transcripts. Sentry's own
 * project settings scrub too; this is the half we control, and it runs
 * first, so a mis-set dashboard still receives nothing to scrub.
 *
 * Two rules, applied to every string anywhere in the event:
 *   - a key that NAMES personal or secret data drops its value whole;
 *   - any string has emails, ids, codes and tokens replaced in place.
 * Query strings are dropped from every URL: they carry return paths,
 * emails and codes, and the error never needs them.
 */

const REDACTED = '[redacted]';

/** Keys whose value is personal or secret whatever it looks like. */
const SENSITIVE_KEY =
  /(pass(word)?|token|secret|auth|cookie|session_?key|api_?key|dsn|email|e_?mail|phone|whatsapp|name|first_?name|family_?name|full_?name|display_?name|transcript|recording|audio|blob|code|otp|join_?code|client_?secret|card|iban|address|body|data_?url)/i;

/** Keys that are safe even though they match the pattern above. */
const SAFE_KEY = /^(type|category|level|status_?code|mechanism|handled|transaction|environment|release|platform|logger|method|url|reason|connection|mode|phase|context|attempts)$/i;

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const JWT = /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/g;
const BEARER = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
/** Sign-in and join codes are short digit runs; 4–8 digits standing alone.
 *  The character before is captured and put back (no lookbehind: Safari
 *  before iOS 16.4 cannot load a module that has one; Web ddb0ce9, 21 Oct). */
const DIGIT_CODE = /(^|[^\w.,:])\d{4,8}(?![\w.,:])/g;
/** Long opaque strings: secrets, signed URLs, client_secret values. */
const OPAQUE = /\b[A-Za-z0-9_-]{32,}\b/g;

export function scrubString(s: string): string {
  return s
    .replace(JWT, REDACTED)
    .replace(BEARER, REDACTED)
    .replace(EMAIL, REDACTED)
    .replace(UUID, ':id')
    .replace(OPAQUE, REDACTED)
    .replace(DIGIT_CODE, (_m, before: string) => before + REDACTED);
}

/** A URL with its query and fragment dropped and its path scrubbed. */
export function scrubUrl(u: string): string {
  const cut = u.search(/[?#]/);
  return scrubString(cut === -1 ? u : u.slice(0, cut));
}

const MAX_DEPTH = 8;

/** Stack-frame fields: code locations, never personal, and mangling
 *  them would break grouping. A path keeps its query dropped. Also the
 *  SDK's own random report and trace ids: redacting them (they are 32
 *  hex characters) gives Sentry an invalid report id, which it may reject. */
const FRAME_KEY = /^(filename|function|module|abs_path|lineno|colno|in_app|event_id|trace_id|span_id|parent_span_id|sid)$/;

function scrubValue(v: unknown, key: string | null, depth: number): unknown {
  if (key !== null && FRAME_KEY.test(key)) {
    return typeof v === 'string' && key === 'abs_path' ? v.replace(/[?#].*$/, '') : v;
  }
  if (key !== null && SENSITIVE_KEY.test(key) && !SAFE_KEY.test(key)) {
    return v === null || v === undefined ? v : REDACTED;
  }
  if (typeof v === 'string') {
    return key !== null && /url|href|from|to$/i.test(key) ? scrubUrl(v) : scrubString(v);
  }
  if (v === null || typeof v !== 'object') return v;
  if (depth >= MAX_DEPTH) return REDACTED;
  if (Array.isArray(v)) return v.map((x) => scrubValue(x, null, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    out[k] = scrubValue(x, k, depth + 1);
  }
  return out;
}

interface EventLike {
  user?: Record<string, unknown> | null;
  request?: Record<string, unknown> & { url?: string };
  breadcrumbs?: Array<Record<string, unknown>>;
  [k: string]: unknown;
}

/**
 * Clean a Sentry event (errors and messages alike). The user block keeps
 * only the hashed id `setTelemetryUser` set; Sentry's own guesses (IP,
 * email, username) are removed.
 */
export function scrubEvent<E extends object>(input: E): E {
  const event = input as EventLike;
  const user = event.user ?? null;
  const hashedId = user && typeof user.id === 'string' && /^h_[0-9a-f]{16}$/.test(user.id) ? user.id : null;

  /* What the SDK itself reports about the browser, OS and device is not
     personal, and "name" there is "Chrome" — keep it. Everything else in
     contexts is ours and gets scrubbed. */
  const contexts = (event.contexts ?? {}) as Record<string, unknown>;
  const sdkContexts: Record<string, unknown> = {};
  const ourContexts: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(contexts)) {
    (/^(browser|os|device|runtime|culture)$/.test(k) ? sdkContexts : ourContexts)[k] = v;
  }

  const cleaned = scrubValue({ ...event, user: undefined, contexts: ourContexts }, null, 0) as EventLike;
  cleaned.user = hashedId ? { id: hashedId } : undefined;
  (cleaned as EventLike).contexts = { ...(cleaned.contexts as Record<string, unknown>), ...sdkContexts };

  if (event.request) {
    const { url } = event.request;
    cleaned.request = { ...(cleaned.request ?? {}), url: typeof url === 'string' ? scrubUrl(url) : url };
    delete (cleaned.request as Record<string, unknown>).cookies;
    delete (cleaned.request as Record<string, unknown>).headers;
    delete (cleaned.request as Record<string, unknown>).query_string;
    delete (cleaned.request as Record<string, unknown>).data;
  }
  return cleaned as E;
}

/**
 * One-way tag for the learner: Sentry can group a learner's errors, and
 * someone holding the real id can hash it to find them, but the id itself
 * never leaves the browser.
 */
export async function hashLearnerId(id: string): Promise<string | null> {
  try {
    const bytes = new TextEncoder().encode(`pb-telemetry:${id}`);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
    return `h_${hex.slice(0, 16)}`;
  } catch {
    return null;
  }
}
